import { Formik } from "formik";
import { object, ref, string } from "yup";

import { useKeystone, usePolicyDetail } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import EditEmailForm from "pages/settings/common/edit-email-form";
import { logException, trackEvent } from "utils";

const initialValues = {
  email: "",
  confirmEmail: "",
};

const schema = object().shape({
  email: string().required("Email required.").email("Enter a valid email."),
  confirmEmail: string()
    .oneOf([ref("email"), undefined], "Email address must match.")
    .required("Email address must match."),
});

type EditBillingEmailModalProps = {
  policyId: string;
  show: boolean;
  handleClose: () => void;
  onEditBillingSuccess: () => void;
};

const EditBillingEmailModal = ({
  policyId,
  show,
  handleClose,
  onEditBillingSuccess,
}: EditBillingEmailModalProps) => {
  const keystone = useKeystone();
  const { mutate: refreshPolicyDetail } = usePolicyDetail(policyId);

  const handleSubmit = async (
    { email },
    { setSubmitting, setStatus, resetForm },
  ) => {
    try {
      setSubmitting(true);

      const updatePaperlessBillingEmailResponse =
        await keystone.updatePaperlessBillingEmail(policyId, email);

      if (isError(updatePaperlessBillingEmailResponse)) {
        throw updatePaperlessBillingEmailResponse;
      } else {
        trackEvent("Email address updated", { paperlessType: "billing" });
        refreshPolicyDetail();
        onEditBillingSuccess();
        resetForm();
      }

      resetForm();
      setSubmitting(false);
    } catch (err) {
      logException(err);
      setStatus(
        "Something went wrong while updating the billing email address.",
      );
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      <EditEmailForm
        handleClose={handleClose}
        show={show}
        heading="Update billing email address"
      />
    </Formik>
  );
};

export default EditBillingEmailModal;
